<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import { TemplatePhonePreview } from 'dashboard/shared/templates/components';

import { WHATSAPP_TEMPLATE_LIMITS } from '../constants/whatsappTemplateTypes';

const props = defineProps({
  body: {
    type: String,
    default: '',
  },
  footer: {
    type: String,
    default: '',
  },
  bodyExamples: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isLastStep: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits([
  'update:body',
  'update:footer',
  'update:bodyExamples',
  'submit',
  'back',
]);
// Regex for extracting variables - note: this is a new instance each time to avoid global state issues
const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;
const ALL_VARIABLES_PATTERN = /\{\{[^}]+\}\}/g;

const { t } = useI18n();

const localBody = ref(props.body);
const localFooter = ref(props.footer);
const localExamples = ref([...props.bodyExamples]);
const bodyTouched = ref(false);

// Sync with props
watch(
  () => props.body,
  newVal => {
    localBody.value = newVal;
  }
);

watch(
  () => props.footer,
  newVal => {
    localFooter.value = newVal;
  }
);

watch(
  () => props.bodyExamples,
  newVal => {
    localExamples.value = [...newVal];
  },
  { deep: true }
);

// Extract variables from body
const variables = computed(() => {
  const matches = localBody.value.match(VARIABLE_PATTERN) || [];
  return matches
    .map(m => {
      const num = parseInt(m.replace(/[{}]/g, ''), 10);
      return { match: m, number: num };
    })
    .sort((a, b) => a.number - b.number);
});

const variablesCount = computed(() => {
  if (variables.value.length === 0) return 0;
  return Math.max(...variables.value.map(v => v.number));
});

// Count non-variable words in text (same logic as Twilio)
const countNonVariableWords = text => {
  if (!text) return 0;

  // Remove all variables from text
  const textWithoutVars = text.replace(ALL_VARIABLES_PATTERN, ' ');

  // Split by whitespace and filter out empty strings
  const words = textWithoutVars
    .split(/\s+/)
    .filter(word => word.trim().length > 0);

  return words.length;
};

// Watch for variable changes and adjust examples array
watch(variablesCount, newCount => {
  const currentLength = localExamples.value.length;

  if (newCount > currentLength) {
    // Add empty strings for new variables
    for (let i = currentLength; i < newCount; i += 1) {
      localExamples.value.push('');
    }
  } else if (newCount < currentLength) {
    // Remove excess examples
    localExamples.value = localExamples.value.slice(0, newCount);
  }

  emit('update:bodyExamples', localExamples.value);
});

const bodyError = computed(() => {
  // Only show required error after user has touched the field
  if (!localBody.value) {
    return bodyTouched.value
      ? t('WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.BODY_REQUIRED')
      : '';
  }

  if (localBody.value.length > WHATSAPP_TEMPLATE_LIMITS.BODY_MAX_LENGTH) {
    return t('WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.BODY_TOO_LONG', {
      max: WHATSAPP_TEMPLATE_LIMITS.BODY_MAX_LENGTH,
    });
  }

  // Check variable format - only numeric variables allowed
  const allPlaceholders = localBody.value.match(ALL_VARIABLES_PATTERN) || [];
  const hasNonNumericVars = allPlaceholders.some(placeholder => {
    const content = placeholder.slice(2, -2); // Remove {{ and }}
    return !/^\d+$/.test(content);
  });
  if (hasNonNumericVars) {
    return t('WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.VARIABLES_ONLY_NUMERIC');
  }

  // Check sequential variables
  if (variables.value.length > 0) {
    const numbers = variables.value.map(v => v.number).sort((a, b) => a - b);
    const expected = Array.from({ length: numbers.length }, (_, i) => i + 1);
    if (JSON.stringify(numbers) !== JSON.stringify(expected)) {
      return t(
        'WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.VARIABLES_NOT_SEQUENTIAL'
      );
    }
  }

  // Check if variables are adjacent (only whitespace between them)
  if (/\}\}\s+\{\{/.test(localBody.value)) {
    return t('WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.VARIABLES_ADJACENT');
  }

  // Check if message starts or ends with a variable
  const trimmed = localBody.value.trim();
  if (/^\{\{[^}]+\}\}/.test(trimmed)) {
    return t('WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.VARIABLES_AT_START');
  }
  if (/\{\{[^}]+\}\}$/.test(trimmed)) {
    return t('WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.VARIABLES_AT_END');
  }

  // Check word-to-variable ratio (2X+1 rule)
  if (variablesCount.value > 0) {
    const wordCount = countNonVariableWords(localBody.value);
    const requiredWords = variablesCount.value * 2 + 1;

    if (wordCount < requiredWords) {
      return t('WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.VARIABLES_RATIO', {
        count: variablesCount.value,
        required: requiredWords,
        current: wordCount,
      });
    }
  }

  return '';
});

const footerError = computed(() => {
  if (
    localFooter.value &&
    localFooter.value.length > WHATSAPP_TEMPLATE_LIMITS.FOOTER_MAX_LENGTH
  ) {
    return t('WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.FOOTER_TOO_LONG', {
      max: WHATSAPP_TEMPLATE_LIMITS.FOOTER_MAX_LENGTH,
    });
  }

  // Use match() instead of test() to avoid global regex state issues
  if (localFooter.value && localFooter.value.match(/\{\{\d+\}\}/)) {
    return t('WHATSAPP_TEMPLATES.CREATE.FORM.ERRORS.FOOTER_HAS_VARIABLES');
  }

  return '';
});

const examplesError = computed(() => {
  if (variablesCount.value > 0) {
    const hasEmptyExamples = localExamples.value.some(
      (ex, idx) => idx < variablesCount.value && !ex
    );
    if (hasEmptyExamples) {
      return t('WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES_HINT');
    }
  }
  return '';
});

const isValid = computed(() => {
  return (
    localBody.value &&
    !bodyError.value &&
    !footerError.value &&
    !examplesError.value
  );
});

// Preview message with variables replaced by examples
const previewMessage = computed(() => {
  let message = localBody.value;
  localExamples.value.forEach((example, index) => {
    const varNum = index + 1;
    const regex = new RegExp(`\\{\\{${varNum}\\}\\}`, 'g');
    message = message.replace(regex, example || `{{${varNum}}}`);
  });
  return message;
});

const insertVariable = () => {
  const nextNum = variablesCount.value + 1;
  localBody.value += `{{${nextNum}}}`;
  emit('update:body', localBody.value);
};

const handleBodyInput = value => {
  bodyTouched.value = true;
  localBody.value = value;
  emit('update:body', value);
};

const handleFooterInput = value => {
  localFooter.value = value;
  emit('update:footer', value);
};

const handleExampleInput = () => {
  emit('update:bodyExamples', localExamples.value);
};

const handleSubmit = () => {
  // Mark field as touched to show any validation errors
  bodyTouched.value = true;
  if (!isValid.value) return;
  emit('submit');
};

const handleBack = () => emit('back');

// Helper functions for templates
const getVariableLabel = n => `{{${n}}}`;
const getVariablePlaceholder = n => `Example for {{${n}}}`;
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Two column layout: Form + Preview -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Left side: Form -->
      <div class="flex flex-col gap-4 order-2 lg:order-1">
        <!-- Message Body -->
        <div class="flex flex-col gap-2">
          <TextArea
            :model-value="localBody"
            :label="t('WHATSAPP_TEMPLATES.CREATE.FORM.BODY_LABEL')"
            :placeholder="t('WHATSAPP_TEMPLATES.CREATE.FORM.BODY_PLACEHOLDER')"
            :max-length="WHATSAPP_TEMPLATE_LIMITS.BODY_MAX_LENGTH"
            show-character-count
            :message="bodyError"
            :message-type="bodyError ? 'error' : 'info'"
            @update:model-value="handleBodyInput"
          />
          <div class="flex items-center justify-between">
            <span
              class="text-xs text-n-slate-11"
              v-html="t('WHATSAPP_TEMPLATES.CREATE.FORM.BODY_HELP')"
            />
            <button
              type="button"
              class="flex items-center gap-1 px-4 py-1.5 text-xs font-medium text-n-brand bg-n-alpha-2 rounded hover:bg-n-alpha-3 transition-colors min-w-[135px] justify-center"
              @click="insertVariable"
            >
              <i class="i-lucide-plus text-xs" />
              {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.ADD_VARIABLE') }}
            </button>
          </div>
          <a
            :href="t('WHATSAPP_TEMPLATES.CREATE.FORM.TUTORIAL_URL')"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-text hover:underline"
          >
            <fluent-icon
              icon="info"
              size="14"
              class="flex-shrink-0 mr-1 text-n-blue-text"
            />
            {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.TUTORIAL') }}
          </a>
        </div>

        <!-- Variable Examples -->
        <div v-if="variablesCount > 0" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES_LABEL') }}
          </label>
          <span class="text-xs text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES_HINT') }}
          </span>
          <div class="flex flex-col gap-3">
            <div
              v-for="n in variablesCount"
              :key="n"
              class="flex flex-col gap-1"
            >
              <Input
                v-model="localExamples[n - 1]"
                :label="getVariableLabel(n)"
                :placeholder="getVariablePlaceholder(n)"
                :message="!localExamples[n - 1] ? 'Required' : ''"
                :message-type="!localExamples[n - 1] ? 'error' : 'info'"
                @input="handleExampleInput()"
              />
            </div>
          </div>
        </div>

        <!-- Footer (optional) -->
        <Input
          v-model="localFooter"
          :label="
            t('WHATSAPP_TEMPLATES.CREATE.FORM.FOOTER_LABEL') + ' (optional)'
          "
          :placeholder="t('WHATSAPP_TEMPLATES.CREATE.FORM.FOOTER_PLACEHOLDER')"
          :message="
            footerError || t('WHATSAPP_TEMPLATES.CREATE.FORM.FOOTER_HINT')
          "
          :message-type="footerError ? 'error' : 'info'"
          @update:model-value="handleFooterInput"
        />
      </div>

      <!-- Right side: Preview -->
      <TemplatePhonePreview
        :body-message="previewMessage"
        :preview-placeholder="
          t('WHATSAPP_TEMPLATES.CREATE.FORM.BODY_PLACEHOLDER')
        "
        :title="t('WHATSAPP_TEMPLATES.CREATE.FORM.PREVIEW_LABEL') || 'Preview'"
        :buttons="[]"
        button-type="quick_reply"
        :footer-text="localFooter"
      />
    </div>

    <!-- Action Buttons -->
    <div class="flex items-center justify-between w-full gap-3 pt-2">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.BACK')"
        icon="i-lucide-arrow-left"
        icon-position="left"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleBack"
      />
      <Button
        :label="
          isLastStep
            ? t('WHATSAPP_TEMPLATES.CREATE.WIZARD.CREATE')
            : t('WHATSAPP_TEMPLATES.CREATE.WIZARD.NEXT')
        "
        :icon="isLastStep ? undefined : 'i-lucide-arrow-right'"
        :icon-position="isLastStep ? undefined : 'right'"
        class="w-full"
        type="button"
        :is-loading="loading"
        :disabled="loading || !isValid"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
